import React from "react"
import { compose } from "redux"
import { RouteComponentProps, withRouter } from "react-router"

// Hooks
import useInactivityDetection from "../../../hooks/useInactivityDetection"

// HOCs


import withInactivityWSEvent, { WithInactivityWSEvent } from "./withInactivityWSEvent"
import { WithRecordsProps } from "./withRecords"
import { RecordTypeEnum } from "../models"
import { WithUserProps } from "./../../../modules/auth/hoc/withUser"


// Constants
import { AppRoute } from "../../routing/constants"

// Context
import {useUiSkin} from "../../ui-skin/context/UiSkinContext"

// Components
import InactivityWarningDialog from "../components/InactivityWarningDialog"
import ModernInactivityDialog from "../components/modern/ModernInactivityDialog"

/*
 * Unchanged values, named. They were written `3000 * 60` and `6000 * 60`, which
 * read as 3000 and 6000 seconds rather than the 3 and 6 minutes they are.
 */
const ECG_IDLE_MS = 3 * 60 * 1000;
const HOLTER_IDLE_MS = 6 * 60 * 1000;

/**
 * @param BaseComponent
 */
export default function withInactivityDetection(BaseComponent: React.ComponentType<any>) {
    function Wrapper(props: RouteComponentProps & WithInactivityWSEvent & WithRecordsProps & WithUserProps) {
        const userInactive = useInactivityDetection();
        const { isModern } = useUiSkin();
        const { history, currentRecord, sendUserWasInactive } = props;
        const [warningDialogOpen, setWarningDialogOpen] = React.useState(false);

        /**
         * The armed idle timer.
         *
         * This was `useState`, and the effect's cleanup closed over the value
         * from the render it was created in — which is the render BEFORE
         * `setTimeoutId` had stored anything, so the cleanup always called
         * `clearTimeout(undefined)`. It happened to work when the doctor simply
         * became active again, because the effect body then cleared the id it
         * read from the next render. It did NOT work when `currentRecord`
         * changed identity while the doctor was idle — which the websocket
         * refetch does routinely: the effect re-ran, the cleanup cancelled
         * nothing, and a SECOND timer was armed alongside the first. On a HOLTER
         * the orphan then fired `history.replace(Home)` and a full page reload
         * on a doctor who was actively working, with no warning dialog first.
         *
         * A ref holds the live id, so the cleanup can always see the timer it is
         * meant to cancel.
         */
        const timeoutRef = React.useRef<number | undefined>(undefined);

        React.useEffect(() => {
            function clear() {
                if (typeof timeoutRef.current !== "undefined") {
                    window.clearTimeout(timeoutRef.current);
                    timeoutRef.current = undefined
                }
            }

            clear();

            const idle = currentRecord !== null && userInactive && warningDialogOpen === false;

            if (idle && currentRecord!.recordType === RecordTypeEnum.ECG) {
                timeoutRef.current = window.setTimeout(() => {
                    timeoutRef.current = undefined;
                    setWarningDialogOpen(true)
                }, ECG_IDLE_MS)
            } else if (idle && currentRecord!.recordType === RecordTypeEnum.HOLTER) {
                timeoutRef.current = window.setTimeout(() => {
                    timeoutRef.current = undefined;
                    history.replace(AppRoute.Home);
                    window.location.reload()
                }, HOLTER_IDLE_MS)
            }

            return clear
            // eslint-disable-next-line
        }, [userInactive, currentRecord, warningDialogOpen]);

        function handleTimeElapsed() {
            setWarningDialogOpen(false);
            sendUserWasInactive();
            history.replace(AppRoute.Home);
        }

        function handleOnClose() {
            setWarningDialogOpen(false);
            props.fetchCurrentUser();
        }

        return (
            <React.Fragment>
                {warningDialogOpen && (
                    isModern ? (
                        <ModernInactivityDialog onTimeElapsed={handleTimeElapsed}
                                                onClose={handleOnClose}/>
                    ) : (
                        <InactivityWarningDialog onTimeElapsed={handleTimeElapsed}
                                                 onClose={handleOnClose}/>
                    )
                )}
                <BaseComponent {...props} />
            </React.Fragment>
        )
    }

    return compose(
        withRouter,
        withInactivityWSEvent,
    )(Wrapper)
}
